import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, X, Ticket, Users, Building2, Loader, List, Monitor, BookOpen } from 'lucide-react';
import { apiFetch, readApiJson } from '../lib/api';
import { formatDisplayName } from '../lib/formatName';

/**
 * GlobalSearch - Search across tickets, users, companies
 */
export default function GlobalSearch({ searchMenuItems }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ tickets: [], users: [], companies: [], devices: [], kb: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Menus correspondant à la requête (synchrone, fourni par la Sidebar).
  // La Sidebar applique déjà le filtrage par droits/org : on ne refiltre pas ici.
  const menuResults = React.useMemo(
    () => (typeof searchMenuItems === 'function' ? searchMenuItems(query) : []),
    [searchMenuItems, query],
  );

  // Liste plate unifiée — ORDRE FIXE : menus, tickets, utilisateurs, sociétés.
  const flatResults = React.useMemo(() => ([
    ...menuResults,
    ...results.tickets.map((t) => ({ ...t, kind: 'ticket' })),
    ...results.users.map((u) => ({ ...u, kind: 'user' })),
    ...results.companies.map((c) => ({ ...c, kind: 'company' })),
    ...results.devices.map((d) => ({ ...d, kind: 'device' })),
    ...results.kb.map((a) => ({ ...a, kind: 'kb' })),
  ]), [menuResults, results]);

  // Réinitialiser la sélection quand la requête change.
  React.useEffect(() => { setActiveIndex(0); }, [query]);

  // Clamp si la liste raccourcit pendant la frappe (résultats async).
  React.useEffect(() => {
    setActiveIndex((i) => (flatResults.length === 0 ? 0 : Math.min(i, flatResults.length - 1)));
  }, [flatResults.length]);

  // Auto-scroll de l'élément actif dans le panneau.
  // Le dropdown est un descendant DOM de containerRef (pas de portail) : recherche scopée au composant.
  React.useEffect(() => {
    const el = containerRef.current?.querySelector?.('[data-keyboard-active="true"]');
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, flatResults.length]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults({ tickets: [], users: [], companies: [], devices: [], kb: [] });
      return;
    }

    // Les requêtes de la frappe précédente sont annulées : une réponse lente ne
    // peut plus écraser la liste de la requête courante.
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const opt = { signal: ctrl.signal };
    const vide = (k) => (err) => { if (err && err.name === 'AbortError') throw err; return { [k]: [] }; };
    setLoading(true);
    try {
      const [ticketsRes, usersRes, companiesRes, devicesRes, kbRes] = await Promise.all([
        apiFetch(`/api/tk/tickets?search=${encodeURIComponent(q)}&limit=5`, opt).then(readApiJson).catch(vide('tickets')),
        apiFetch(`/api/tk/admin/users?search=${encodeURIComponent(q)}&limit=5`, opt).then(readApiJson).catch(vide('users')),
        apiFetch(`/api/tk/admin/clients?search=${encodeURIComponent(q)}&limit=5`, opt).then(readApiJson).catch(vide('clients')),
        // Postes et articles KB (proposition UI validée le 07/09/2026)
        apiFetch(`/api/tk/devices/search?q=${encodeURIComponent(q)}&limit=5`, opt).then(readApiJson).catch(vide('items')),
        apiFetch(`/api/kb?search=${encodeURIComponent(q)}`, opt).then(readApiJson).catch(vide('articles')),
      ]);
      if (ctrl.signal.aborted) return;
      setResults({
        tickets: ticketsRes.tickets || [],
        users: usersRes.users || [],
        companies: companiesRes.clients || [],
        devices: (devicesRes.items || []).slice(0, 5),
        kb: (kbRes.articles || []).slice(0, 5),
      });
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('Search failed:', err);
    }
    if (!ctrl.signal.aborted) setLoading(false);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const updateDropdownPos = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 360) });
    }
  };

  const handleFocus = () => {
    setOpen(true);
    updateDropdownPos();
  };

  // URL canonique (vraie route SPA) pour chaque type de résultat.
  // Sert à la fois de href des <Link> (clic droit / nouvel onglet / survol) et à
  // la navigation clavier. Utilisateurs/sociétés pointent vers leur fiche détail
  // /:id : les anciens `?edit=` n'étaient lus par aucune page et retombaient sur
  // la liste — c'est aussi la cible du bouton « éditer » de la page Utilisateurs.
  const buildHref = (kind, item) => {
    switch (kind) {
      case 'menu': return item.path;
      case 'ticket': return `/tickets/${item.id}`;
      case 'user': return `/gestion/utilisateurs/${item.id}`;
      case 'company': return `/gestion/societes/${item.id}`;
      // `fiche_id` = destination publiée par la page Ordinateurs (identifiant ESET
      // quand le poste en a un). `id` reste la clé d'inventaire, celle qui rattache
      // un poste à un ticket : les deux ne désignent pas la même chose. Sans cette
      // distinction, un même poste physique avait deux URL — incident AALYAH 09/09.
      case 'device': return `/parc/ordinateurs/${item.fiche_id || item.id}`;
      case 'kb': return `/kb/article/${item.id}`;
      default: return null;
    }
  };

  const handleSelect = (type, item) => {
    const href = buildHref(type, item);
    setOpen(false);
    setQuery('');
    if (href) navigate(href);
  };

  // onClick des <Link>. La navigation SPA du clic gauche simple est assurée par
  // <Link> lui-même (rendu d'un vrai <a href>) : ici on ne fait QUE le nettoyage
  // UI. Sur un clic modifié (Ctrl/⌘/Shift/Alt ou bouton non-gauche → nouvel
  // onglet), on n'intervient pas : panneau et requête restent en place.
  const handleResultClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    setOpen(false);
    setQuery('');
  };

  const executeResult = (item) => {
    if (!item) return;
    handleSelect(item.kind, item);
  };

  const hasResults =
    menuResults.length > 0 ||
    results.tickets.length > 0 || results.users.length > 0 || results.companies.length > 0 || results.devices.length > 0 || results.kb.length > 0;
  // Les menus matchent dès 1 caractère ; les appels API à partir de 2.
  const showDropdown = open && (query.trim().length >= 2 || menuResults.length > 0);

  // Index global de départ de chaque groupe — suit l'ORDRE FIXE de flatResults
  // (menus, tickets, utilisateurs, sociétés). Calculé une fois, pas par groupe.
  const base = {
    menus: 0,
    tickets: menuResults.length,
    users: menuResults.length + results.tickets.length,
    companies: menuResults.length + results.tickets.length + results.users.length,
    devices: menuResults.length + results.tickets.length + results.users.length + results.companies.length,
    kb: menuResults.length + results.tickets.length + results.users.length + results.companies.length + results.devices.length,
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Search Input */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: open ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        padding: '7px 12px',
        minWidth: 200,
        transition: 'all var(--duration-fast) var(--ease-out)',
        border: open ? '1px solid rgba(255,255,255,0.2)' : '1px solid transparent',
      }}>
        {loading ? (
          <Loader size={15} style={{ color: 'rgba(255,255,255,0.6)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        ) : (
          <Search size={15} style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); updateDropdownPos(); }}
          onFocus={handleFocus}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, Math.max(flatResults.length - 1, 0)));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              executeResult(flatResults[activeIndex] || flatResults[0]);
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
              inputRef.current?.blur();
            }
          }}
          placeholder="Rechercher... (Ctrl+K)"
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#fff',
            fontSize: 13,
            width: '100%',
            minWidth: 150,
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults({ tickets: [], users: [], companies: [], devices: [], kb: [] }); }}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer',
              padding: 2, display: 'flex', borderRadius: 4,
              transition: 'background var(--duration-fast)',
            }}
          >
            <X size={12} style={{ color: 'rgba(255,255,255,0.6)' }} />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {showDropdown && (
        <div className="dropdown-animate" style={{
          position: 'fixed',
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          background: 'var(--surface)',
          borderRadius: 10,
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--border-light)',
          zIndex: 1000,
          overflow: 'hidden',
          maxHeight: 420,
          overflowY: 'auto',
        }}>
          {!hasResults && !loading && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Aucun resultat pour "{query}"
            </div>
          )}

          {/* Menus */}
          {menuResults.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <List size={13} /> Menus ({menuResults.length})
              </div>
              {menuResults.map((m, i) => {
                const gi = base.menus + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={m.path}
                    to={buildHref('menu', m)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {m.icon ? <m.icon size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> : null}
                      <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>{m.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{m.sectionLabel}</div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Tickets */}
          {results.tickets.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <Ticket size={13} /> Tickets ({results.tickets.length})
              </div>
              {results.tickets.map((t, i) => {
                const gi = base.tickets + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={t.id}
                    to={buildHref('ticket', t)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>
                      #{t.number} - {t.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {t.status} · {t.priority}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Users */}
          {results.users.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <Users size={13} /> Utilisateurs ({results.users.length})
              </div>
              {results.users.map((u, i) => {
                const gi = base.users + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={u.id}
                    to={buildHref('user', u)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>
                      {formatDisplayName(u.first_name, u.last_name)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {u.email} · {u.role}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Companies */}
          {results.companies.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <Building2 size={13} /> Societes ({results.companies.length})
              </div>
              {results.companies.map((c, i) => {
                const gi = base.companies + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={c.id}
                    to={buildHref('company', c)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {c.city || '-'} · {c.email_domain || '-'}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Postes (proposition UI validée le 07/09/2026) */}
          {results.devices.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <Monitor size={13} /> Postes ({results.devices.length})
              </div>
              {results.devices.map((d, i) => {
                const gi = base.devices + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={d.id}
                    to={buildHref('device', d)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>{d.hostname || d.name || d.label || d.id}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {d.client_name || d.client || d.company || '-'}{d.serial_number ? ` · ${d.serial_number}` : ''}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Articles KB */}
          {results.kb.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <BookOpen size={13} /> Base de connaissances ({results.kb.length})
              </div>
              {results.kb.map((a, i) => {
                const gi = base.kb + i;
                const isActive = gi === activeIndex;
                return (
                  <Link
                    key={a.id}
                    to={buildHref('kb', a)}
                    data-keyboard-active={isActive || undefined}
                    onMouseEnter={() => setActiveIndex(gi)}
                    onClick={handleResultClick}
                    style={{ ...resultLink, background: isActive ? 'var(--bg)' : undefined }}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--primary)', fontSize: 13 }}>{a.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {(a.category_path || a.category || 'KB')}{a.status ? ` · ${a.status}` : ''}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const sectionHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: 'var(--bg)',
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid var(--border-light)',
};

const resultItem = {
  padding: '10px 14px',
  cursor: 'pointer',
  borderBottom: '1px solid var(--bg)',
  transition: 'background var(--duration-fast) var(--ease-out)',
};

// Variante <Link> : un <a> doit annuler le style de lien par défaut (soulignement,
// couleur bleue, inline) pour rester visuellement identique aux anciennes lignes.
const resultLink = {
  ...resultItem,
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
};
